import React, { useState, useEffect } from 'react';
import { Map, Marker } from 'pigeon-maps';
import { getCompanyLogoSrc } from '../utils/mediaUrl';

export default function CompanyEdit() {
  const [formData, setFormData] = useState({
    companyName: '',
    companyWebsite: '',
    industry: '',
    houseNumber: '',
    streetAddress: '',
    city: '',
    county: '',
    postalCode: '',
  });
  const [geoTag, setGeoTag] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [logo, setLogo] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    fetchCompanyInformation();
    // eslint-disable-next-line
  }, []);

  // Helper to parse GeoTag string to [lat, lng] array
  const parseGeoTag = (geoTagString) => {
    if (!geoTagString) return null;
    if (Array.isArray(geoTagString)) return geoTagString;
    if (typeof geoTagString === 'string') {
      const parts = geoTagString.split(',').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return parts;
      }
    }
    return null;
  };

  const fetchCompanyInformation = async () => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/company`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch company information');
      const data = await response.json();
      if (data.success) {
        setCompanyData(data.data[0]);
        setGeoTag(parseGeoTag(data.data[0]?.GeoTag));
        setLogo(getCompanyLogoSrc(data.data[0]) || '');
      }
    } catch (error) {
      console.error('Error fetching company information:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Separate POST for geotag
  const handleGeoTagUpdate = async () => {
    if (!geoTag) return;
    try {
      const response = await fetch('https://api.insightos.co.uk/api/companygeotag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ GeoTag: geoTag.join(',')}),
      });
      if (response.ok) {
        alert('Geotag updated!');
        fetchCompanyInformation();
      }
    } catch (error) {
      alert('Error updating geotag.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const companyPayload = {
      CompanyName: formData.companyName,
      Website: formData.companyWebsite,
      CompanyNumber: formData.houseNumber,
      Industry: formData.industry,
      Address: `${formData.streetAddress}, ${formData.city}, ${formData.county}, ${formData.postalCode}`,
      City: formData.city,
      CompanyCode: companyData?.CompanyCode,
    };
    try {
      const response = await fetch('https://api.insightos.co.uk/api/companyedit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(companyPayload),
      });
      if (response.ok) {
        alert('Company information updated!');
        setShowForm(false);
        fetchCompanyInformation();
      }
    } catch (error) {
      alert('Error updating company info.');
    }
  };

  const handleLogoUpload = async (file) => {
    const formDataData = new FormData();
    formDataData.append('file', file);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/companypic/${companyData?.CompanyCode}`, {
        method: 'POST',
        credentials: 'include',
        body: formDataData,
      });
      if (response.ok) {
        fetchCompanyInformation();
      } else {
        alert('Error uploading logo.');
      }
    } catch (error) {
      alert('Error uploading logo.');
    }
  };

  const GeoLocate = () => {
    navigator.geolocation.getCurrentPosition(function (position) {
      setGeoTag([position.coords.latitude, position.coords.longitude]);
    });
  };

  return (
    <div className="mt-8 rounded-3xl border border-white/35 bg-gradient-to-br from-indigo-100/75 via-white/75 to-emerald-100/70 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:p-8">
      <div className="w-full rounded-2xl border border-white/35 bg-white/80 p-6 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-10">
        {!showForm && companyData && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
              <div className="relative">
                <div className="rounded-full bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 p-1 shadow-[0_12px_30px_rgba(49,46,129,0.3)]">
                  {logo ? (
                    <img
                      src={logo}
                      alt="Company Logo"
                      className="h-24 w-24 rounded-full border-4 border-white/40 object-cover shadow-xl"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-indigo-950 to-indigo-800 text-4xl font-bold text-indigo-200">
                      🏢
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="mb-2 text-2xl font-extrabold tracking-wide text-slate-900 sm:text-3xl">{companyData.CompanyName}</h2>
                <p className="mb-1 text-base text-indigo-700">{companyData.Website}</p>
                <p className="mb-1 text-base text-sky-700">{companyData.Industry}</p>
                <p className="text-sm text-slate-600">{companyData.Address}</p>
                <p className="mt-2 text-xs text-slate-500">Companies House #: {companyData.HouseNumber}</p>
              </div>
            </div>
            {geoTag && Array.isArray(geoTag) && (
              <div className="mb-6 overflow-hidden rounded-2xl border border-white/35 shadow-[0_10px_26px_rgba(15,23,42,0.12)]">
                <Map height={200} defaultCenter={geoTag} center={geoTag} defaultZoom={15}>
                  <Marker width={40} anchor={geoTag} />
                </Map>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="touch-manipulation flex-1 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-6 py-3 font-bold text-white shadow-[0_12px_30px_rgba(49,46,129,0.3)] transition-all duration-200 hover:from-indigo-800/95 hover:to-indigo-600/95 active:scale-95"
                onClick={() => setShowForm(true)}
              >
                Edit Company Information
              </button>
              <button
                className="touch-manipulation flex-1 rounded-xl border border-white/20 bg-gradient-to-br from-sky-600/90 to-sky-500/90 px-6 py-3 font-bold text-white shadow-[0_12px_30px_rgba(14,116,144,0.28)] transition-all duration-200 hover:from-sky-500/95 hover:to-sky-400/95 active:scale-95"
                onClick={handleGeoTagUpdate}
                data-component="company-edit-actions"
                data-feature="update_geotag_post"
              >
                Update Geotag
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="companyName" className="mb-2 block text-sm font-semibold text-slate-700">Company Name</label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="StaffShark"
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 placeholder-slate-400 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="companyWebsite" className="mb-2 block text-sm font-semibold text-slate-700">Company Website</label>
                <input
                  id="companyWebsite"
                  name="companyWebsite"
                  type="text"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 placeholder-slate-400 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="industry" className="mb-2 block text-sm font-semibold text-slate-700">Industry</label>
                <input
                  id="industry"
                  name="industry"
                  type="text"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="Hospitality"
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 placeholder-slate-400 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="houseNumber" className="mb-2 block text-sm font-semibold text-slate-700">Companies House Number</label>
                <input
                  id="houseNumber"
                  name="houseNumber"
                  type="text"
                  value={formData.houseNumber}
                  onChange={handleChange}
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="streetAddress" className="mb-2 block text-sm font-semibold text-slate-700">Street Address</label>
                <input
                  id="streetAddress"
                  name="streetAddress"
                  type="text"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="city" className="mb-2 block text-sm font-semibold text-slate-700">City</label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleChange}
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="county" className="mb-2 block text-sm font-semibold text-slate-700">County</label>
                <input
                  id="county"
                  name="county"
                  type="text"
                  value={formData.county}
                  onChange={handleChange}
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label htmlFor="postalCode" className="mb-2 block text-sm font-semibold text-slate-700">Postal Code</label>
                <input
                  id="postalCode"
                  name="postalCode"
                  type="text"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className="block w-full rounded-xl border border-white/35 bg-white/85 px-4 py-3 font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center mt-4">
              <button
                type="button"
                onClick={GeoLocate}
                className="touch-manipulation flex-1 rounded-xl border border-white/20 bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 px-6 py-3 font-bold text-white shadow-[0_12px_30px_rgba(5,150,105,0.28)] transition-all duration-200 hover:from-emerald-500/95 hover:to-emerald-400/95 active:scale-95"
              >
                Set Geo Tag Location
              </button>
              <label className="touch-manipulation flex-1 cursor-pointer rounded-xl border border-white/20 bg-gradient-to-br from-sky-600/90 to-sky-500/90 px-6 py-3 font-bold text-white shadow-[0_12px_30px_rgba(14,116,144,0.28)] transition-all duration-200 hover:from-sky-500/95 hover:to-sky-400/95 active:scale-95">
                Upload Logo
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLogoFile(file);
                      handleLogoUpload(file);
                    }
                  }}
                />
              </label>
            </div>
            {geoTag && Array.isArray(geoTag) && (
              <div className="mt-6 overflow-hidden rounded-2xl border border-white/35 shadow-[0_10px_26px_rgba(15,23,42,0.12)]">
                <Map height={200} defaultCenter={geoTag} center={geoTag} defaultZoom={15}>
                  <Marker width={40} anchor={geoTag} />
                </Map>
              </div>
            )}
            {logo && (
              <div className="mt-6 flex justify-center">
                <img
                  src={logo}
                  alt="Company Logo"
                  className="w-24 h-24 object-cover rounded-full border-4 border-white/30 shadow-xl"
                />
              </div>
            )}
            <div className="mt-8 flex items-center justify-end gap-x-6">
              <button
                type="button"
                className="text-sm font-semibold text-slate-500 transition hover:text-slate-700"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-component="company-edit-form"
                data-feature="save_company_info_post"
                className="rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-6 py-3 text-sm font-bold text-white shadow-[0_12px_30px_rgba(49,46,129,0.3)] transition-all duration-200 hover:from-indigo-800/95 hover:to-indigo-600/95"
              >
                Save Changes
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}